export const listingFiltersMethods = {
    getFilterUiRoots() {
        return [
            this.filtersSidebar?.content,
            this.desktopFiltersRoot ||
                this.root?.querySelector('[data-listing-desktop-filters]'),
        ].filter(Boolean);
    },

    syncPendingFiltersFromState() {
        this.pendingFilters = this.cloneFilters(this.state.filters || {});
        this.expandPendingHierarchicalFilters();
        this.expandPendingFilterAncestors();
        this.updatePendingFilterSelectionUi();
    },

    getSidebarFilterTaxonomies() {
        const taxonomies = new Set(this.getFilterTaxonomies());

        this.getFilterUiRoots().forEach(root => {
            root
                .querySelectorAll('[data-listing-filter-select][data-taxonomy]')
                .forEach(button => {
                    const taxonomy = button.dataset.taxonomy || '';

                    if (taxonomy) {
                        taxonomies.add(taxonomy);
                    }
                });
        });

        return Array.from(taxonomies);
    },

    resetPendingSidebarFilters() {
        this.pendingFilters = {};

        this.getSidebarFilterTaxonomies().forEach(taxonomy => {
            this.pendingFilters[taxonomy] = [];
        });
    },

    getEmptyListingFilters() {
        return this.getSidebarFilterTaxonomies().reduce((acc, taxonomy) => {
            acc[taxonomy] = [];
            return acc;
        }, {});
    },

    clearSidebarFilters({ applyToListing = false } = {}) {
        this.resetPendingSidebarFilters();
        this.updatePendingFilterSelectionUi();
        this.schedulePendingFiltersCountPreview();

        if (!applyToListing) {
            return;
        }

        this.state.filters = this.getEmptyListingFilters();
        this.state.page = 1;
        this.filtersSidebar?.close();
        this.fetchAndRender({ pushUrl: this.getListingUrl() });
    },

    clearAppliedListingFilters() {
        this.state.filters = this.getEmptyListingFilters();
        this.state.page = 1;
        this.clearSidebarFilters({ applyToListing: false });
        this.filtersSidebar?.close();
        this.fetchAndRender({ pushUrl: this.getListingUrl() });
    },

    usesHierarchicalFilterSelection(taxonomy) {
        return taxonomy === 'product_cat';
    },

    isSlugCoveredByPendingSelection(taxonomy, slug) {
        if (!taxonomy || !slug) {
            return false;
        }

        const pending = this.pendingFilters[taxonomy] || [];

        if (pending.includes(slug)) {
            return true;
        }

        if (!this.usesHierarchicalFilterSelection(taxonomy)) {
            return false;
        }

        const descendantsMap = this.bootstrap.termDescendants?.[taxonomy] || {};

        return pending.some(selectedSlug => {
            const branch = descendantsMap[selectedSlug];

            return Array.isArray(branch) && branch.includes(slug);
        });
    },

    getPendingListingState() {
        return {
            ...this.state,
            filters: this.cloneFilters(this.pendingFilters),
            page: 1,
        };
    },

    schedulePendingFiltersCountPreview() {
        if (this.pendingFiltersCountTimer) {
            window.clearTimeout(this.pendingFiltersCountTimer);
        }

        this.pendingFiltersCountTimer = window.setTimeout(() => {
            this.pendingFiltersCountTimer = null;
            this.updatePendingFiltersApplyLabel();
        }, 300);
    },

    getFiltersApplyLabelElement() {
        return (
            this.filtersSidebar?.content?.querySelector(
                '[data-listing-filters-apply-label]'
            ) || null
        );
    },

    formatFiltersApplyLabel(total) {
        const normalized = Math.max(0, Number(total) || 0);

        return `Wyświetl ${normalized.toLocaleString('pl-PL')}`;
    },

    async updatePendingFiltersApplyLabel() {
        const label = this.getFiltersApplyLabelElement();

        if (!label) {
            return;
        }

        const requestId = ++this.pendingFiltersCountRequestId;

        label.textContent = 'Wyświetl …';

        try {
            const total = await this.fetchListingCount(
                this.getPendingListingState()
            );

            if (requestId !== this.pendingFiltersCountRequestId) {
                return;
            }

            label.textContent = this.formatFiltersApplyLabel(total);
            this.syncPendingFilterTreeCounts(total);
        } catch (error) {
            console.warn('Listing filter count preview failed.', error);

            if (requestId !== this.pendingFiltersCountRequestId) {
                return;
            }

            const fallbackTotal = Number(this.total?.dataset.total || 0);
            label.textContent = this.formatFiltersApplyLabel(fallbackTotal);
            this.syncPendingFilterTreeCounts(fallbackTotal);
        }
    },

    hasFilterCountsInSidebar() {
        return this.getFilterUiRoots().some(root =>
            Array.from(
                root.querySelectorAll('[data-listing-filter-label]')
            ).some(label => /\(\d[\d\s.]*\)/.test(label.textContent || ''))
        );
    },

    buildFilterCountsRequestBody() {
        const listingState = {
            ...this.state,
            page: 1,
        };
        const body = this.buildElasticRequestBody(listingState);
        body.listing = 'filterCounts';
        const terms = [];

        this.getFilterUiRoots().forEach(root => {
            root
                .querySelectorAll(
                    '[data-listing-filter-select][data-term-slug]'
                )
                .forEach(button => {
                    const taxonomy = button.dataset.taxonomy || '';
                    const slug = button.dataset.termSlug || '';

                    if (!taxonomy || !slug) {
                        return;
                    }

                    terms.push({ taxonomy, slug });
                });
        });

        return {
            ...body,
            terms,
        };
    },

    async loadFilterTermCounts({ force = false } = {}) {
        if (
            !this.canUseElastic() ||
            (!force && (this.filterCountsLoaded || this.filterCountsLoading))
        ) {
            return;
        }

        const requestBody = this.buildFilterCountsRequestBody();
        const terms = requestBody.terms || [];

        if (terms.length === 0) {
            return;
        }

        this.filterCountsLoading = true;

        try {
            const elasticHeaders = {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            };

            if (this.bootstrap.elasticApiToken) {
                elasticHeaders['X-Listing-Token'] =
                    this.bootstrap.elasticApiToken;
            }

            const elasticResponse = await fetch(this.bootstrap.elasticApiUrl, {
                method: 'POST',
                headers: elasticHeaders,
                body: JSON.stringify(requestBody),
            });

            if (!elasticResponse.ok) {
                throw new Error('Elastic filter counts request failed.');
            }

            const elasticData = await elasticResponse.json();

            if (!elasticData?.success || !elasticData.counts) {
                throw new Error(
                    'Elastic filter counts response was not successful.'
                );
            }

            this.applyFilterCounts(elasticData.counts);
            this.filterCountsLoaded = true;
        } catch (error) {
            console.warn('Listing filter counts failed.', error);
        } finally {
            this.filterCountsLoading = false;
        }
    },

    getVisibleFilterSelectSelector() {
        return '[data-listing-filter-select]:not(.hidden):not([hidden])';
    },

    setListingFilterOptionVisibility(button, visible) {
        if (!button) {
            return;
        }

        button.classList.toggle('hidden', !visible);

        if (visible) {
            button.removeAttribute('hidden');
            button.removeAttribute('aria-hidden');
        } else {
            button.setAttribute('hidden', '');
            button.setAttribute('aria-hidden', 'true');
        }
    },

    applyFilterCounts(counts = {}) {
        this.getFilterUiRoots().forEach(sidebarRoot => {
            if (!sidebarRoot) {
                return;
            }

            sidebarRoot
                .querySelectorAll(
                    '[data-listing-filter-select][data-term-slug]'
                )
                .forEach(button => {
                    const taxonomy = button.dataset.taxonomy || '';
                    const slug = button.dataset.termSlug || '';
                    const key = `${taxonomy}:${slug}`;
                    const count = Object.prototype.hasOwnProperty.call(
                        counts,
                        key
                    )
                        ? Math.max(0, Number(counts[key]) || 0)
                        : 0;
                    const isSelected = this.isFilterSelectedInUi(
                        taxonomy,
                        slug
                    );
                    const visible = count > 0 || isSelected;

                    this.setListingFilterOptionVisibility(button, visible);

                    if (!visible) {
                        return;
                    }

                    const labelNode = button.querySelector(
                        '[data-listing-filter-label]'
                    );

                    if (!labelNode) {
                        return;
                    }

                    const termName =
                        this.bootstrap.termIndex?.[taxonomy]?.[slug]?.name ||
                        '';
                    const labelText =
                        (labelNode.textContent || '')
                            .replace(/\s*\([\d\s.]+\)\s*$/, '')
                            .trim() || termName;

                    labelNode.textContent = `${labelText} (${count.toLocaleString(
                        'pl-PL'
                    )})`;
                });

            if (Object.keys(counts).length > 0) {
                this.pruneEmptyListingFilterGroups(sidebarRoot);
                this.pruneEmptyFlatFilterGroups(sidebarRoot);
                this.pruneEmptyDesktopFilters(sidebarRoot);
            }
        });
    },

    pruneEmptyListingFilterGroups(sidebarRoot) {
        const visibleSelector = this.getVisibleFilterSelectSelector();

        sidebarRoot
            .querySelectorAll('[data-listing-filter-group]')
            .forEach(group => {
                const tree = group.querySelector('.listing__filter-tree');

                if (!tree) {
                    return;
                }

                const hasVisibleOptions = Boolean(
                    tree.querySelector(visibleSelector)
                );

                group.classList.toggle('hidden', !hasVisibleOptions);
            });

        sidebarRoot
            .querySelectorAll('[data-listing-filter-children]')
            .forEach(container => {
                const tree = container.querySelector(
                    ':scope > .listing__filter-tree'
                );

                if (!tree) {
                    return;
                }

                const hasVisibleOptions = Boolean(
                    tree.querySelector(visibleSelector)
                );

                if (!hasVisibleOptions) {
                    container.classList.add('hidden');
                    container
                        .closest('[data-listing-filter-node]')
                        ?.querySelector('[data-listing-filter-toggle]')
                        ?.setAttribute('aria-expanded', 'false');
                }
            });
    },

    pruneEmptyFlatFilterGroups(sidebarRoot) {
        const visibleSelector = this.getVisibleFilterSelectSelector();

        sidebarRoot
            .querySelectorAll('[data-listing-filter-group]')
            .forEach(group => {
                if (group.querySelector('[data-listing-sort-option]')) {
                    return;
                }

                if (group.querySelector('.listing__filter-tree')) {
                    return;
                }

                const hasVisibleOptions = Boolean(
                    group.querySelector(visibleSelector)
                );

                group.classList.toggle('hidden', !hasVisibleOptions);
            });
    },

    pruneEmptyDesktopFilters(sidebarRoot) {
        const visibleSelector = this.getVisibleFilterSelectSelector();

        sidebarRoot
            .querySelectorAll('[data-listing-desktop-filter]')
            .forEach(filterRoot => {
                const hasVisibleOptions = Boolean(
                    filterRoot.querySelector(visibleSelector)
                );

                filterRoot.classList.toggle('hidden', !hasVisibleOptions);
            });
    },

    syncPendingFilterTreeCounts(total = 0) {
        const activeSelections = Object.entries(
            this.pendingFilters || {}
        ).filter(([, value]) => {
            if (Array.isArray(value)) {
                return value.length > 0;
            }

            return Boolean(value);
        });

        if (activeSelections.length !== 1) {
            return;
        }

        const [taxonomy, rawValues] = activeSelections[0];
        const slugs = Array.isArray(rawValues) ? rawValues : [rawValues];
        const normalizedSlugs = slugs.filter(Boolean);

        if (normalizedSlugs.length !== 1) {
            return;
        }

        const slug = normalizedSlugs[0];
        const button = this.filtersSidebar?.content?.querySelector(
            `[data-listing-filter-select][data-taxonomy="${taxonomy}"][data-term-slug="${slug}"]`
        );
        const labelNode = button?.querySelector('.listing__filter-label');

        if (!labelNode) {
            return;
        }

        const labelText = labelNode.textContent
            .replace(/\s*\([\d\s.]+\)\s*$/, '')
            .trim();
        const formattedTotal = Math.max(0, Number(total) || 0).toLocaleString(
            'pl-PL'
        );

        labelNode.textContent = `${labelText} (${formattedTotal})`;
    },

    isFilterSelectedInUi(taxonomy, slug) {
        if (
            Object.prototype.hasOwnProperty.call(this.pendingFilters, taxonomy)
        ) {
            return this.isSlugCoveredByPendingSelection(taxonomy, slug);
        }

        const stateValues = this.state.filters?.[taxonomy];

        if (
            Object.prototype.hasOwnProperty.call(
                this.state.filters || {},
                taxonomy
            )
        ) {
            if (Array.isArray(stateValues) && stateValues.length === 0) {
                return false;
            }

            if (stateValues) {
                const slugs = Array.isArray(stateValues)
                    ? stateValues
                    : [stateValues];
                const descendantsMap =
                    this.bootstrap.termDescendants?.[taxonomy] || {};

                return slugs.some(selectedSlug => {
                    if (selectedSlug === slug) {
                        return true;
                    }

                    if (!this.usesHierarchicalFilterSelection(taxonomy)) {
                        return false;
                    }

                    return (descendantsMap[selectedSlug] || []).includes(slug);
                });
            }

            return false;
        }

        return false;
    },

    updatePendingFilterSelectionUi() {
        this.getFilterUiRoots().forEach(root => {
            root
                .querySelectorAll('[data-listing-filter-select]')
                .forEach(button => {
                    const taxonomy = button.dataset.taxonomy || '';
                    const slug = button.dataset.termSlug || '';
                    const isSelected = this.isFilterSelectedInUi(
                        taxonomy,
                        slug
                    );
                    const checkbox = button.querySelector(
                        '.listing__filter-checkbox'
                    );
                    const indicator = button.querySelector(
                        '.listing__filter-checkbox-icon'
                    );

                    button.setAttribute(
                        'aria-pressed',
                        isSelected ? 'true' : 'false'
                    );

                    if (indicator) {
                        indicator.classList.toggle('hidden!', !isSelected);
                        indicator.classList.toggle('text-grey-900', isSelected);
                    }

                    if (checkbox) {
                        checkbox.classList.toggle(
                            'border-background-accent',
                            isSelected
                        );
                        checkbox.classList.toggle(
                            'bg-background-accent',
                            isSelected
                        );
                        checkbox.classList.toggle(
                            'border-grey-900',
                            !isSelected
                        );
                        checkbox.classList.toggle('bg-white', !isSelected);
                    }
                });
        });
    },

    expandPendingHierarchicalFilters() {
        this.filtersSidebar?.content
            ?.querySelectorAll('[data-listing-filter-select][data-term-slug]')
            .forEach(button => {
                const taxonomy = button.dataset.taxonomy || '';
                const slug = button.dataset.termSlug || '';

                if (!this.usesHierarchicalFilterSelection(taxonomy)) {
                    return;
                }

                if (
                    !taxonomy ||
                    !slug ||
                    !(this.pendingFilters[taxonomy] || []).includes(slug)
                ) {
                    return;
                }

                const node = button.closest('[data-listing-filter-node]');

                if (!node) {
                    return;
                }

                const expanded = new Set(this.pendingFilters[taxonomy] || []);
                this.getFilterNodeSlugs(node).forEach(item =>
                    expanded.add(item)
                );
                this.pendingFilters[taxonomy] = this.sortFilterValuesByDomOrder(
                    taxonomy,
                    Array.from(expanded)
                );
            });
    },

    expandPendingFilterAncestors() {
        this.filtersSidebar?.content
            ?.querySelectorAll('[data-listing-filter-select][data-term-slug]')
            .forEach(button => {
                const taxonomy = button.dataset.taxonomy || '';
                const slug = button.dataset.termSlug || '';

                if (
                    !taxonomy ||
                    !slug ||
                    !(this.pendingFilters[taxonomy] || []).includes(slug)
                ) {
                    return;
                }

                let parentNode = button
                    .closest('[data-listing-filter-node]')
                    ?.parentElement?.closest('[data-listing-filter-node]');

                while (parentNode) {
                    const toggle = parentNode.querySelector(
                        ':scope > .listing__filter-row [data-listing-filter-toggle]'
                    );
                    const content = parentNode.querySelector(
                        ':scope > [data-listing-filter-children]'
                    );
                    const icon = toggle?.querySelector('i');

                    if (content) {
                        content.classList.remove('hidden');
                    }

                    if (toggle) {
                        toggle.setAttribute('aria-expanded', 'true');
                    }

                    icon?.classList.add('rotate-180');

                    parentNode = parentNode.parentElement?.closest(
                        '[data-listing-filter-node]'
                    );
                }
            });
    },

    getFilterNodeSlugs(node) {
        return Array.from(
            node.querySelectorAll(
                '[data-listing-filter-select][data-term-slug]'
            )
        )
            .map(button => button.dataset.termSlug || '')
            .filter(Boolean);
    },

    sortFilterValuesByDomOrder(taxonomy, values) {
        const domOrder = Array.from(
            this.filtersSidebar?.content?.querySelectorAll(
                `[data-listing-filter-select][data-taxonomy="${taxonomy}"][data-term-slug]`
            ) || []
        )
            .map(button => button.dataset.termSlug || '')
            .filter(Boolean);

        return Array.from(new Set(values)).sort((left, right) => {
            const leftIndex = domOrder.indexOf(left);
            const rightIndex = domOrder.indexOf(right);

            if (leftIndex === -1 || rightIndex === -1) {
                return left.localeCompare(right, 'pl');
            }

            return leftIndex - rightIndex;
        });
    },

    cloneFilters(filters) {
        return Object.entries(filters || {}).reduce((acc, [key, value]) => {
            acc[key] = Array.isArray(value) ? [...value] : value;
            return acc;
        }, {});
    },

    mergeFilters(...filterSets) {
        return filterSets.reduce((acc, filters) => {
            Object.entries(filters || {}).forEach(([taxonomy, value]) => {
                const values = Array.isArray(value) ? value : [value];
                const nextValues = [
                    ...(Array.isArray(acc[taxonomy]) ? acc[taxonomy] : []),
                    ...values.filter(Boolean),
                ];

                if (nextValues.length === 0) {
                    return;
                }

                acc[taxonomy] = this.sortFilterValuesByDomOrder(
                    taxonomy,
                    Array.from(new Set(nextValues))
                );
            });

            return acc;
        }, {});
    },

    collectSelectedFiltersFromMarkup() {
        const filters = {};

        this.filtersSidebar?.content
            ?.querySelectorAll(
                '[data-listing-filter-select][aria-pressed="true"]'
            )
            .forEach(button => {
                const taxonomy = button.dataset.taxonomy || '';
                const slug = button.dataset.termSlug || '';

                if (!taxonomy || !slug) {
                    return;
                }

                if (!Array.isArray(filters[taxonomy])) {
                    filters[taxonomy] = [];
                }

                filters[taxonomy].push(slug);
            });

        return Object.entries(filters).reduce((acc, [taxonomy, values]) => {
            acc[taxonomy] = this.sortFilterValuesByDomOrder(
                taxonomy,
                Array.from(new Set(values))
            );
            return acc;
        }, {});
    }
};
